import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

/**
 * This type is used to indicate a sample-type of interest (e.g., for filters)
 */

export enum SampleFilterType {
  BATCH = 'BATCH',
  BIOENTITY = 'BIOENTITY',
  BOOLEAN = 'BOOLEAN',
  BOX = 'BOX',
  CONTAINER = 'CONTAINER',
  FLOAT = 'FLOAT',
  INTEGER = 'INTEGER',
  PLATE = 'PLATE',
  // TODO(BNCH-96009): support additional type Location
  STORABLE = 'STORABLE', // Matches BOX or PLATE or CONTAINER
  STRING = 'STRING',
  WELL = 'WELL', // Plate + position doubleton
  // TODO(BNCH-68478): support additional types: BLOB_LINK, DATE, DATETIME, JSON, LONG_TEXT, SELECTOR
}

export interface SampleFilter {
  // The coarsest filter is simply a category (e.g., any bioentity)
  type: SampleFilterType;

  // Fine-grained filters can specify an exact schema id
  schemaId?: string | null;

  // Interfaces ids that schemaId fulfills
  parentInterfaceIds?: Array<string>;
}

export type SampleFilters = Array<SampleFilter>;

const filterTypes: Record<SampleFilterType, SampleFilter> = {
  BATCH: {type: SampleFilterType.BATCH},
  BIOENTITY: {type: SampleFilterType.BIOENTITY},
  BOOLEAN: {type: SampleFilterType.BOOLEAN},
  BOX: {type: SampleFilterType.BOX},
  CONTAINER: {type: SampleFilterType.CONTAINER},
  FLOAT: {type: SampleFilterType.FLOAT},
  INTEGER: {type: SampleFilterType.INTEGER},
  PLATE: {type: SampleFilterType.PLATE},
  STORABLE: {type: SampleFilterType.STORABLE},
  STRING: {type: SampleFilterType.STRING},
  WELL: {type: SampleFilterType.WELL},
  // TODO(BNCH-68478): support additional types: BLOB_LINK, DATE, DATETIME, JSON, LONG_TEXT, SELECTOR
};

// Some convenient shortcuts when you need only a coarse filter
export const SimpleSampleFilters: Record<SampleFilterType, SampleFilter> = filterTypes;

export function dedupeSampleFilters(inputFilters: Array<SampleFilter>): Array<SampleFilter> {
  return _.unique(inputFilters, (filter) => `${filter.type}~${filter.schemaId ?? 'none'}`);
}

/**
 * Returns whether needle (source filter type) is matched by any filter in haystack (destination filters). Does not do schema id matching.
 * E.g. Are there any plate filters in haystack?
 */
export function isTypeInSampleFilters(haystack: Array<SampleFilter>, needle: SampleFilterType): boolean {
  // TODO(BNCH-96009): add SampleFilterType.Location when we support Locations in AFST
  const storableFilterTypes = [SampleFilterType.CONTAINER, SampleFilterType.BOX, SampleFilterType.PLATE];
  return haystack.some(
    (destFilter) =>
      destFilter.type === needle ||
      // Also allow integers (source column) to go into floats (destination column)
      (needle === SampleFilterType.INTEGER && destFilter.type === SampleFilterType.FLOAT) ||
      // Allow transferring between the generic "STORABLE" filter and any of the specific storable types
      (needle === SampleFilterType.STORABLE && storableFilterTypes.includes(destFilter.type)) ||
      (destFilter.type === SampleFilterType.STORABLE && storableFilterTypes.includes(needle))
  );
}

/**
 * Returns whether any source filter matches any destination filter.
 * If both the source and destination require a specific schema, then their required schemas must match.
 * They would also match if either the source or destination does not require a schema.
 */
export function isFilterInSampleFilters(
  destinationFilters: Array<SampleFilter>,
  sourceFilters: Array<SampleFilter>
): boolean {
  return destinationFilters.some((dest) =>
    sourceFilters.some(
      (source) =>
        isTypeInSampleFilters([dest], source.type) &&
        (dest.schemaId == null ||
          source.schemaId == null ||
          source.schemaId === dest.schemaId ||
          source.parentInterfaceIds?.some((parentInterfaceId) => parentInterfaceId === dest.schemaId))
    )
  );
}

/**
 * Return the appropriate icon name for a given sample filter.
 */
export function sampleFilterToIcon(sampleFilter: SampleFilter): BenchlingIconName {
  const typeToIcon: Record<SampleFilterType, BenchlingIconName> = {
    [SampleFilterType.BATCH]: ModelTypeConstants.BATCH,
    [SampleFilterType.BIOENTITY]: FolderItemIcon.BasicFolderItem,
    [SampleFilterType.BOOLEAN]: 'boolean',
    [SampleFilterType.BOX]: ModelTypeConstants.BOX,
    [SampleFilterType.CONTAINER]: ModelTypeConstants.LOCATION,
    [SampleFilterType.FLOAT]: 'number',
    [SampleFilterType.INTEGER]: 'number',
    [SampleFilterType.PLATE]: ModelTypeConstants.PLATE,
    [SampleFilterType.STORABLE]: ModelTypeConstants.LOCATION,
    [SampleFilterType.STRING]: 'text',
    [SampleFilterType.WELL]: 'vial',
  };

  return typeToIcon[sampleFilter.type];
}
